// CATÁLOGO v3 — el vigente. 3 pilares / 12 conceptos / 44 subconceptos.
//
// Contenido cerrado por Sergio. Táctico DESAPARECE del catálogo: queda sujeto
// al paquete de videoanálisis, que no existe hoy. Técnico se sustituye ENTERO
// por el vocabulario MCB/MSB de la estructura de entrenamiento del club —no es
// una edición, no sobrevive ningún subconcepto del v2—. Físico se reduce y
// mental se depura de 12 subconceptos a 9.
//
// Las medias siguen siendo media de medias, como en el v2: la del concepto sale
// de sus subconceptos y la del pilar de sus conceptos. Todos los conceptos
// pesan igual dentro de su pilar aunque tengan 1 subconcepto o 6 (Fuerza pesa
// lo mismo que Patrones motores). Decisión de Jose.
//
// El radar mantiene un vértice por CONCEPTO: pasa de 21 a 12. Decisión de Jose.
//
// LAS CLAVES DE PILAR SON PROPIAS, NO LAS DEL v2
// ----------------------------------------------
// `v3_tecnico` y no `tecnico`. No es cosmética: con claves propias, mezclar
// versiones deja de ser una regla que hay que recordar y pasa a ser
// IMPOSIBLE — una fila v2 simplemente no tiene los pilares del v3, así que
// ningún código puede juntarlas por clave sin que se note. El `v3_` es
// estructural, no contenido.
//
// LAS CLAVES DE SUBCONCEPTO SE DERIVAN DEL NOMBRE, EN CASTELLANO
// -------------------------------------------------------------
// El v2 usaba claves en inglés (`short_passing`, `with_teammates`). Aquí no,
// por dos razones: lo pidió Jose («las claves se derivan de los subconceptos»)
// y porque MCB/MSB es vocabulario de metodología del club. Traducir «Esconder
// intenciones» a `hide_intentions` sería inventarme una terminología que nadie
// ha escrito, en una clave que se persiste para siempre.
//
// «ESCONDER INTENCIONES» SE REPITE EN TRES CONCEPTOS Y NO COLISIONA
// ----------------------------------------------------------------
// El espacio de nombres de subconcepto es POR CONCEPTO: en el JSONB vive en
// `<pilar>.categories.<concepto>.subs.<sub>`, así que son tres rutas distintas
// (`v3_tecnico.categories.mcb_control.subs.esconder_intenciones` y sus dos
// hermanas). No hace falta acotarlas, y prefijarlas rompería la lectura obvia
// de que las tres son el mismo criterio aplicado a tres acciones.
//
// Los cuatro conceptos de mental REUTILIZAN las claves del v2
// (`communication`, `confidence`, `concentration`, `professionalism`) porque
// significan lo mismo y `RADAR_CATEGORY_ABBR` se indexa por clave de concepto:
// compartir la abreviatura del radar es lo correcto, no una colisión.

import type { Catalogo, PillarDef } from './evaluationCatalog';

const PILARES_V3: PillarDef[] = [
  {
    key: 'v3_tecnico',
    label: 'Técnico',
    color: '#2196F3',
    categories: [
      {
        key: 'mcb_control',
        label: 'MCB · Control',
        subs: [
          { key: 'escanear', label: 'Escanear' },
          { key: 'perfil_corporal', label: 'Perfil corporal' },
          { key: 'control_orientado', label: 'Control orientado' },
          { key: 'control_para_fijar', label: 'Control para fijar' },
          { key: 'esconder_intenciones', label: 'Esconder intenciones' },
          { key: 'dar_tiempo', label: 'Dar tiempo' },
        ],
      },
      {
        key: 'mcb_pase',
        label: 'MCB · Pase',
        subs: [
          { key: 'intencion_del_pase', label: 'Intención del pase' },
          { key: 'tension_del_pase', label: 'Tensión del pase' },
          { key: 'pase_corto', label: 'Pase corto' },
          { key: 'pase_medio_largo', label: 'Pase medio/largo' },
          { key: 'esconder_intenciones', label: 'Esconder intenciones' },
        ],
      },
      {
        key: 'mcb_conduccion',
        label: 'MCB · Conducción',
        subs: [
          { key: 'conduccion_para_fijar', label: 'Conducción para fijar' },
          { key: 'conduccion_para_superar', label: 'Conducción para superar' },
          { key: 'gestion_de_los_ritmos', label: 'Gestión de los ritmos' },
          { key: 'con_pierna_alejada', label: 'Con pierna alejada' },
          { key: 'cortar_trayectorias', label: 'Cortar trayectorias' },
          { key: 'esconder_intenciones', label: 'Esconder intenciones' },
        ],
      },
      {
        key: 'mcb_proteccion',
        label: 'MCB · Protección',
        subs: [
          { key: 'proteccion_quedarse', label: 'Protección (quedarse)' },
          { key: 'proteccion_girarse', label: 'Protección (girarse)' },
          { key: 'uso_de_brazos', label: 'Uso de brazos' },
          { key: 'dos_carreras', label: 'Dos carreras' },
        ],
      },
      {
        key: 'mcb_tiro',
        label: 'MCB · Tiro',
        subs: [
          { key: 'definir', label: 'Definir' },
          { key: 'finalizar', label: 'Finalizar' },
          { key: 'remates_a_un_contacto', label: 'Remates a un contacto' },
          { key: 'remates_con_alturas', label: 'Remates con alturas' },
        ],
      },
      {
        key: 'msb_rti',
        label: 'MSB · RTI',
        subs: [
          { key: 'despejes_rasos', label: 'Despejes rasos' },
          { key: 'despejes_aereos', label: 'Despejes aéreos' },
          { key: 'defensa_de_giros', label: 'Defensa de giros' },
          { key: 'hacerse_grande', label: 'Hacerse grande' },
          { key: 'detalles_micro_defensivos', label: 'Detalles micro defensivos' },
        ],
      },
    ],
  },
  {
    key: 'v3_fisico',
    label: 'Físico',
    color: '#FF5722',
    categories: [
      {
        key: 'fuerza',
        label: 'Fuerza',
        subs: [{ key: 'fuerza_en_la_lucha', label: 'Fuerza en la lucha' }],
      },
      {
        key: 'patrones_motores',
        label: 'Patrones motores',
        subs: [
          { key: 'patron_de_aceleracion', label: 'Patrón de aceleración' },
          { key: 'patron_de_frenada', label: 'Patrón de frenada' },
          { key: 'patron_de_cambios_de_direccion', label: 'Patrón de cambios de dirección' },
          { key: 'patron_de_salto', label: 'Patrón de salto' },
        ],
      },
    ],
  },
  {
    key: 'v3_mental',
    label: 'Mental',
    color: '#FFF200',
    categories: [
      {
        key: 'communication',
        label: 'Comunicación',
        subs: [
          { key: 'con_companeros', label: 'Con compañeros' },
          { key: 'con_cuerpo_tecnico', label: 'Con cuerpo técnico' },
        ],
      },
      {
        key: 'confidence',
        label: 'Confianza',
        subs: [
          { key: 'gestion_del_error', label: 'Gestión del error' },
          { key: 'gestion_del_feedback', label: 'Gestión del feedback' },
        ],
      },
      {
        key: 'concentration',
        label: 'Concentración',
        subs: [
          { key: 'mantener_el_foco', label: 'Mantener el foco' },
          { key: 'duracion', label: 'Duración' },
        ],
      },
      {
        key: 'professionalism',
        label: 'Profesionalidad',
        subs: [
          { key: 'actitud_en_entreno', label: 'Actitud en entreno' },
          { key: 'compromiso_puntualidad', label: 'Compromiso-puntualidad' },
          { key: 'habitos', label: 'Hábitos' },
        ],
      },
    ],
  },
];

export const CATALOGO_V3: Catalogo = { version: 3, pilares: PILARES_V3 };
